// ─── SSE job-frame handler — split out for seam-testability ────────────────
//
// This module exists for ONE reason: the SSE onmessage body has to be
// directly invocable by a seam test (web/static/spa/test/sse-handler.seam.test.mjs)
// so the parity-with-polling invariant is pin-tested. Putting it in init.ts
// is impossible — init.ts is the SPA entry script with massive top-level
// side effects (Supabase boot, OAuth listeners, EventSource construction).
// Importing init.ts from Node trips every one of those.
//
// PARITY INVARIANT (code-hygiene-sweep finding HIGH #2, 2026-05-23):
// Both this SSE handler and `polling.mjs:pollLoop` consume the same server
// job-frame payload. They MUST mirror each other on:
//
//   1. CANCELLED branch → remove from jobState AND clear monotonic cache.
//   2. Status transition to terminal → clear monotonic cache.
//
// The original SSE handler skipped BOTH cache-clears, causing every "remediate
// again" retry of a previously-completed job.id to re-arm at the OLD
// monotonic-pct floor. The polling handler (polling.mjs:95, polling.mjs:108-111)
// has always done both — terminal frames typically arrive via SSE first in
// production, so the cache entry leaked every time the SSE path won the race.
//
// Future drift defence: see web/static/spa/test/sse-handler.seam.test.mjs
// for the parity-test pattern. Any new side-effect call added to the polling
// terminal branch MUST be mirrored here (and the parity test extended).
import { jobState } from './job-state.js';
/** Process one parsed SSE job-frame.
 *
 *  Reads the same `window.MainJobStatus` / `window.MAIN_JOB_TERMINAL_STATUSES`
 *  globals that polling.mjs reads (they're initialised by globals-boot via
 *  the legacy main-job-status.js import path).
 *
 *  Mirrors polling.mjs:pollLoop's per-job branch (the parity invariant —
 *  see file header). Specifically:
 *    - CANCELLED frame → removeJob + saveJobIds + clearMonotonicCache
 *      + render + refreshSystemBanner.
 *    - Cleared-job frame → ignored (handled before transition check).
 *    - Any other frame → trackJob; on terminal-status transition, clear
 *      the monotonic cache.
 */
export function handleSseFrame(job, deps) {
    if (!job?.id)
        return;
    // Resolve status constants: prefer injected deps (testable); fall back to
    // window globals (production path where init.ts wires the constants).
    let CANCELLED = deps.cancelledStatus;
    let TERMINAL = deps.terminalStatuses;
    if (CANCELLED === undefined || TERMINAL === undefined) {
        const win = (typeof window !== 'undefined' ? window : {});
        if (CANCELLED === undefined)
            CANCELLED = win.MainJobStatus?.CANCELLED;
        if (TERMINAL === undefined)
            TERMINAL = win.MAIN_JOB_TERMINAL_STATUSES;
    }
    // ── CANCELLED branch — mirror polling.mjs:92-97 ────────────────────────
    if (CANCELLED && job.status === CANCELLED) {
        if (jobState.hasJob(job.id)) {
            deps.log.info(`[ada-tool] SSE: job ${job.id} cancelled — removing from UI`);
            jobState.removeJob(job.id);
            // PARITY: polling clears monotonic cache on CANCELLED too.
            jobState.clearMonotonicCache(job.id);
            jobState.saveJobIds();
            deps.render();
            deps.refreshSystemBanner();
        }
        return;
    }
    if (jobState.isCleared(job.id))
        return;
    const prev = jobState.getJob(job.id);
    const prevStatus = prev?.status;
    const updated = jobState.trackJob(job, { eventTs: Date.now() });
    if (updated && prev && prevStatus !== updated.status) {
        deps.log.info(`[ada-tool] SSE: job ${job.id} (${String(updated.filename)}): ${prevStatus} → ${updated.status}`);
        // PARITY: polling.mjs:108-111 clears the monotonic cache on a
        // transition INTO a terminal status. Mirror that here.
        if (TERMINAL?.has(updated.status)) {
            jobState.clearMonotonicCache(job.id);
        }
    }
    else if (updated && !prev) {
        deps.log.info(`[ada-tool] SSE: new job ${job.id} (${String(updated.filename)}): ${updated.status}`);
    }
    jobState.saveJobIds();
    deps.render();
    deps.refreshSystemBanner();
}
